import { useState } from 'react';
import { Link } from 'react-router-dom';
import { whyChooseCards, comparisonData, safetyTips, faqItems, mainSiteUrl } from '@/mocks/categories';

function WhatIsSection() {
  return (
    <section className="py-14 md:py-20 bg-background-100/80">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Czym jest Kakobuy Spreadsheet?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            Kakobuy Spreadsheet to specjalnie przygotowany arkusz kalkulacyjny, który pomaga użytkownikom
            szybko znajdować najlepsze produkty dostępne przez platformę Kakobuy. Zamiast spędzać godziny
            na przeszukiwaniu chińskich platform, możesz w kilka sekund znaleźć sprawdzone i popularne produkty.
          </p>
        </div>

        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <table className="w-full max-w-3xl mx-auto border-collapse">
            <thead>
              <tr className="border-b-2 border-background-300/60">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-700">Funkcja</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-500">Tradycyjne wyszukiwanie</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-primary-600">Kakobuy Spreadsheet</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, idx) => (
                <tr key={idx} className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-sm text-foreground-700 font-medium">{row.feature}</td>
                  <td className="py-3 px-4 text-sm text-foreground-500">{row.traditional}</td>
                  <td className="py-3 px-4 text-sm text-primary-600 font-medium">{row.spreadsheet}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function WhyChooseSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Dlaczego warto wybrać Kakobuy Spreadsheet?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Odkryj korzyści korzystania z gotowych arkuszy produktowych.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {whyChooseCards.map((card, idx) => (
            <div
              key={idx}
              className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 hover:border-primary-200/50 transition-all duration-300"
            >
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                <i className={`${card.icon} text-lg md:text-xl text-primary-500`}></i>
              </div>
              <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-2">{card.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SafetySection() {
  return (
    <section className="py-14 md:py-20 bg-background-100/80">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Czy Kakobuy Spreadsheet jest bezpieczny?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Dowiedz się jak bezpiecznie korzystać z arkuszy i unikać potencjalnych zagrożeń.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="space-y-3">
            {safetyTips.map((tip, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5"
              >
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-shield-flash-line text-lg text-red-400"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-800 mb-1">{tip.risk}</p>
                  <p className="text-sm text-foreground-600">{tip.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ArticleCTASection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="relative bg-primary-500/5 border border-primary-200/40 rounded-2xl p-8 md:p-12 text-center overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-400/10 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent-400/10 rounded-full translate-y-1/2 -translate-x-1/3 blur-3xl"></div>

          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Chcesz dowiedzieć się więcej?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto mb-6">
              Przeczytaj nasz szczegółowy artykuł o Kakobuy Spreadsheet — wszystko co musisz wiedzieć przed zakupami.
            </p>

            <Link
              to="/jak-korzystac-z-kakobuy-spreadsheet"
              className="inline-flex items-center px-8 py-3.5 bg-foreground-950 text-background-50 font-semibold text-sm md:text-base rounded-xl hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-article-line mr-2"></i>
              Czytaj pełny artykuł
            </Link>

            <div className="flex items-center justify-center gap-4 mt-4">
              <Link to="/poradnik-bezpieczenstwa-kakobuy" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-shield-check-line mr-1"></i>Poradnik bezpieczeństwa
              </Link>
              <Link to="/czym-jest-kakobuy-spreadsheet" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-question-line mr-1"></i>Czym jest Spreadsheet?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20 bg-background-100/80">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Często Zadawane Pytania
          </h2>
          <p className="text-sm md:text-base text-foreground-600">
            Odpowiadamy na najczęstsze pytania dotyczące Kakobuy Spreadsheet.
          </p>
        </div>

        <div className="space-y-2.5">
          {faqItems.map((item, idx) => (
            <div
              key={idx}
              className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
              >
                <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                  {item.q}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-foreground-400 text-lg transition-transform duration-200 flex-shrink-0 ${
                    openIndex === idx ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  openIndex === idx ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {item.a}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ContentSections() {
  return (
    <>
      <WhatIsSection />
      <WhyChooseSection />
      <SafetySection />
      <ArticleCTASection />
      <FAQSection />
    </>
  );
}